import { get, useFormContext, useWatch } from 'react-hook-form'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import HFDropdown from 'components/HFInputs/HFDropdown'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import HFMultiSelect from 'components/HFInputs/HFMultipleSelect'
import HFTextInput from 'components/HFInputs/HFTextInput'

import styles from './RuleEntryCondition.module.sass'
import { getFieldOptions, isNumeric } from '../RuleSchema'
import { useMemo } from 'react'

const fieldSelectorColors = [
	styles.textColor, // placeholder
	styles.textColorStrong, // selected text and icon
	styles.foregroundColor, // background
	styles.foregroundColor, // border
	styles.themeColor, // border::hover
].join(';')

const textInputColors = [
	styles.textColorStrong,
	styles.textColorStrong,
	styles.foregroundColor,
	styles.backgroundColor,
	'transparent',
].join(';')

const typeSelectorColors = [
	styles.warningColor,
	styles.warningColor,
	styles.warningBackgroundColor,
	styles.warningBackgroundColor,
].join(';')

const RuleEntryConditionCell = ({
	path,
	options,
	actions,
	option: initialOption,
	optionGroup: initialOptionGroup,
}) => {
	const { i18n } = useGlobal()

	const { formState, setValue } = useFormContext()
	const errors = get(formState.errors ?? {}, path)

	// eslint-disable-next-line no-undef
	const [option = initialOption, optionGroup = initialOptionGroup] = useWatch(
		{
			name: [`${path}.option`, `${path}.optionGroup`],
		}
	)

	const fieldValueOptions = getFieldOptions(option, options)
	const hasFieldOptions = Number(fieldValueOptions?.length) > 0

	const fieldOptions = useMemo(() => {
		return Array.isArray(options.fields)
			? {
					options: options.fields,
			  }
			: {
					groups: Object.keys(options.fields).map((key) => ({
						value: key,
						text: i18n.translate(key),
					})),
					groupOptions: options.fields[optionGroup] ?? [],
			  }
	}, [optionGroup, options.fields, i18n])

	const numericField = isNumeric(option)
	const operators = option
		? isNumeric(option)
			? options.numericOperators
			: hasFieldOptions
			? options.listOperators
			: options.stringOperators ?? []
		: []

	const error = useMemo(() => {
		return (errors?.option ?? errors?.type ?? errors?.value)?.message
	}, [errors])

	return (
		<span className={styles['logical-condition-cell']} data-error={error}>
			<VStack
				className={`${styles['logical-condition-cell-content']} ${
					numericField ? styles['--is-numeric'] : ''
				}`}
				justify='space-between'
			>
				{fieldOptions.options ? (
					<HFDropdown
						name={`${path}.option`}
						className={styles['selector']}
						tint={fieldSelectorColors}
						options={fieldOptions.options}
						optionsAlign='right'
					/>
				) : (
					<HStack gap='xsmall'>
						<HFDropdown
							name={`${path}.optionGroup`}
							className={styles['selector']}
							tint={fieldSelectorColors}
							options={fieldOptions.groups}
							optionsAlign='right'
							onChange={(e) => {
								setValue(
									path,
									{
										optionGroup: e.target.value,
										option: null,
										operator: null,
										value: null,
									},
									{
										shouldDirty: true,
										shouldTouch: true,
									}
								)
							}}
						/>
						<HFDropdown
							name={`${path}.option`}
							className={`${styles['selector']} ${styles['flex-grow']}`}
							tint={fieldSelectorColors}
							options={fieldOptions.groupOptions}
							optionsAlign='right'
						/>
					</HStack>
				)}
				<HStack gap='xsmall'>
					<HFDropdown
						className={`${styles['selector']} ${styles['rounded']}`}
						tint={typeSelectorColors}
						name={`${path}.operator`}
						options={operators}
						optionsAlign='right'
					/>
					{(numericField || !hasFieldOptions) && (
						<HFTextInput
							className={styles['text-input']}
							name={`${path}.value`}
							tint={textInputColors}
							ignoreCustomValidity={true}
						/>
					)}
					{!numericField && hasFieldOptions && (
						<HFMultiSelect
							className={styles['value-selector']}
							placeholder={i18n`Select options`}
							name={`${path}.value`}
							options={fieldValueOptions}
							searchable
						/>
					)}
				</HStack>
			</VStack>
			{actions}
		</span>
	)
}

export default RuleEntryConditionCell
